// frontend/src/components/projects/workspaces/MathProjectWorkspace.tsx
// Matematik ve Mantık dersleri için Matematiksel Modelleme ve Problem Çözüm Atölyesi

import { useState, useEffect } from 'react';

export interface MathWorkData {
  givens: string;
  target: string;
  formula: string;
  steps: string[];
  finalResult: string;
  verification: string;
}

interface MathProjectWorkspaceProps {
  initialContent: string;
  projectTitle: string;
  projectDescription: string | null;
  onChange: (formattedText: string) => void;
  disabled?: boolean;
}

export function MathProjectWorkspace({
  initialContent,
  projectTitle,
  projectDescription,
  onChange,
  disabled = false,
}: MathProjectWorkspaceProps) {
  const [data, setData] = useState<MathWorkData>(() => {
    try {
      if (initialContent.startsWith('{') && initialContent.includes('givens')) {
        return JSON.parse(initialContent);
      }
    } catch {
      // Fallback
    }
    return {
      givens: '',
      target: '',
      formula: '',
      steps: ['1. Bilinen değişkenleri yerleştir.', '2. Denklem ve işlem adımlarını uygula.'],
      finalResult: '',
      verification: '',
    };
  });

  const [activeTab, setActiveTab] = useState<'editor' | 'board'>('editor');

  useEffect(() => {
    const formatted = `### 📐 MATEMATİKSEL MODELLEME VE PROBLEM ÇÖZÜM RAPORU
**Proje:** ${projectTitle}

#### 1. Verilenler ve İstenenler
- **Verilenler (Bilinenler):** ${data.givens || '(Belirtilmedi)'}
- **İstenen Değer (Hedef):** ${data.target || '(Belirtilmedi)'}

#### 2. Matematiksel Bağıntı & Formül
${data.formula || '(Formül belirtilmedi)'}

#### 3. Adım Adım İşlem Basamakları
${data.steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}

#### 4. Nihai Sonuç
${data.finalResult || '(Sonuç girilmedi)'}

#### 5. Sağlama & Mantıksal Doğrulama
${data.verification || '(Sağlama yapılmadı)'}
`;
    onChange(formatted);
  }, [data, projectTitle, onChange]);

  function updateField<K extends keyof MathWorkData>(key: K, value: MathWorkData[K]) {
    setData((prev) => ({ ...prev, [key]: value }));
  }

  function handleStepChange(index: number, val: string) {
    const newSteps = [...data.steps];
    newSteps[index] = val;
    updateField('steps', newSteps);
  }

  function addStep() {
    updateField('steps', [...data.steps, '']);
  }

  function removeStep(index: number) {
    if (data.steps.length <= 1) return;
    updateField('steps', data.steps.filter((_, i) => i !== index));
  }

  return (
    <div className="math-workspace-container">
      {/* Sol Panel: Matematiksel Çözüm Formu */}
      <div className="math-editor-panel card">
        <div className="math-panel-header">
          <div className="math-badge">📐 Matematiksel Modelleme & Çözüm Atölyesi</div>
          <div className="math-tabs">
            <button
              type="button"
              className={`math-tab-btn ${activeTab === 'editor' ? 'active' : ''}`}
              onClick={() => setActiveTab('editor')}
            >
              ✏️ Çözüm Basamakları
            </button>
            <button
              type="button"
              className={`math-tab-btn ${activeTab === 'board' ? 'active' : ''}`}
              onClick={() => setActiveTab('board')}
            >
              📋 Çözüm Tahtası
            </button>
          </div>
        </div>

        {activeTab === 'editor' ? (
          <div className="math-form-body">
            {/* 1. Verilenler ve İstenenler */}
            <div className="math-grid-2">
              <div className="math-form-group">
                <label className="math-label">
                  <span>1. Verilenler (Bilinen Değerler)</span>
                  <span className="math-hint">Örn: a = 12 cm, b = 8 cm, hız = 60 km/s...</span>
                </label>
                <textarea
                  className="input math-textarea"
                  rows={3}
                  placeholder="Problemde verilen sayısal veya geometrik değerleri listele..."
                  value={data.givens}
                  onChange={(e) => updateField('givens', e.target.value)}
                  disabled={disabled}
                />
              </div>

              <div className="math-form-group">
                <label className="math-label">
                  <span>2. İstenen Değer (Hedef)</span>
                  <span className="math-hint">Örn: Üçgenin alanı (A = ?), Toplam maliyet...</span>
                </label>
                <textarea
                  className="input math-textarea"
                  rows={3}
                  placeholder="Hesaplanması ve bulunması hedeflenen nihai büyüklüğü yaz..."
                  value={data.target}
                  onChange={(e) => updateField('target', e.target.value)}
                  disabled={disabled}
                />
              </div>
            </div>

            {/* 2. Formül ve Matematiksel Kural */}
            <div className="math-form-group">
              <label className="math-label">
                <span>3. Matematiksel Bağıntı, Kural veya Denklem</span>
                <span className="math-hint">Örn: Alan = (taban * yükseklik) / 2 | Hız = Yol / Zaman | f(x) = ax + b</span>
              </label>
              <input
                type="text"
                className="input"
                placeholder="Kullanacağın formülü, teoremi veya cebirsel bağıntıyı yaz..."
                value={data.formula}
                onChange={(e) => updateField('formula', e.target.value)}
                disabled={disabled}
              />
            </div>

            {/* 3. Adım Adım İşlem Basamakları */}
            <div className="math-form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label className="math-label" style={{ margin: 0 }}>
                  <span>4. Adım Adım Hesaplama ve Çözüm Basamakları</span>
                </label>
                <button type="button" className="math-add-btn" onClick={addStep} disabled={disabled}>
                  + İşlem Adımı Ekle
                </button>
              </div>
              <div className="math-steps-list">
                {data.steps.map((step, idx) => (
                  <div key={idx} className="math-step-row">
                    <span className="math-step-num">{idx + 1}</span>
                    <input
                      type="text"
                      className="input"
                      style={{ flex: 1, fontFamily: 'var(--f-mono)' }}
                      placeholder={`${idx + 1}. işlem adımı: Örn: Alan = (12 * 8) / 2 = 96 / 2 = 48 cm²`}
                      value={step}
                      onChange={(e) => handleStepChange(idx, e.target.value)}
                      disabled={disabled}
                    />
                    {data.steps.length > 1 && (
                      <button
                        type="button"
                        className="math-del-btn"
                        onClick={() => removeStep(idx)}
                        disabled={disabled}
                        title="Adımı Sil"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* 4. Sonuç ve Sağlama */}
            <div className="math-grid-2">
              <div className="math-form-group">
                <label className="math-label">
                  <span>5. Nihai Sonuç (Birimli)</span>
                  <span className="math-hint">Örn: 48 cm², 120 km, 15 kişi</span>
                </label>
                <input
                  type="text"
                  className="input"
                  style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0EA5E9' }}
                  placeholder="Bulunan nihai net sonuç..."
                  value={data.finalResult}
                  onChange={(e) => updateField('finalResult', e.target.value)}
                  disabled={disabled}
                />
              </div>

              <div className="math-form-group">
                <label className="math-label">
                  <span>6. Sağlama & Mantıksal Doğrulama</span>
                  <span className="math-hint">Sonucun mantıklılığını nasıl test ettin?</span>
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="Örn: Ters işlemle kontrol edildi: 48 * 2 = 96..."
                  value={data.verification}
                  onChange={(e) => updateField('verification', e.target.value)}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="math-board-view">
            <div className="math-board-header">
              <h2>📐 Matematik Çözüm Tahtası</h2>
              <div className="math-board-title">{projectTitle}</div>
            </div>
            <div className="math-board-box">
              <div className="math-board-row">
                <span className="math-board-lbl">Verilenler:</span>
                <span className="math-board-val">{data.givens || '—'}</span>
              </div>
              <div className="math-board-row">
                <span className="math-board-lbl">İstenen:</span>
                <span className="math-board-val">{data.target || '—'}</span>
              </div>
              <div className="math-board-row">
                <span className="math-board-lbl">Model/Formül:</span>
                <span className="math-board-val" style={{ fontFamily: 'var(--f-mono)', color: '#2563EB' }}>
                  {data.formula || '—'}
                </span>
              </div>
            </div>

            <div className="math-board-section">
              <h4>📝 İşlem Basamakları</h4>
              <div className="math-board-steps">
                {data.steps.map((st, i) => (
                  <div key={i} className="math-board-step-item">
                    <span className="step-idx">{i + 1}.</span>
                    <span className="step-txt">{st || 'Boş adım'}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="math-board-result-box">
              <div>
                <span className="result-lbl">NİHAİ SONUÇ:</span>
                <span className="result-val">{data.finalResult || 'Hesaplanmadı'}</span>
              </div>
              {data.verification && (
                <div className="result-verify">
                  <span>Sağlama: {data.verification}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Sağ Panel: Matematiksel Modelleme Kontrol Listesi */}
      <div className="math-side-panel">
        <div className="card math-guide-card">
          <h3>📐 Matematik Çözüm Kriterleri</h3>
          <p className="math-guide-desc">
            Matematik projelerinde formülün netliği ve işlem adımlarının düzeni en yüksek puanı belirler.
          </p>
          <div className="math-checklist">
            <div className={`math-check-item ${data.givens.trim() && data.target.trim() ? 'done' : ''}`}>
              <span className="check-icon">{data.givens.trim() && data.target.trim() ? '✅' : '⚪'}</span>
              <div>
                <strong>Problemi Ayrıştır</strong>
                <p>Verilenleri ve aranan değişkeni net listele.</p>
              </div>
            </div>
            <div className={`math-check-item ${data.formula.trim() ? 'done' : ''}`}>
              <span className="check-icon">{data.formula.trim() ? '✅' : '⚪'}</span>
              <div>
                <strong>Uygun Formülü Seç</strong>
                <p>Kuralı, bağıntıyı veya cebirsel denklemi belirt.</p>
              </div>
            </div>
            <div className={`math-check-item ${data.steps.some(s => s.trim().length > 5) ? 'done' : ''}`}>
              <span className="check-icon">{data.steps.some(s => s.trim().length > 5) ? '✅' : '⚪'}</span>
              <div>
                <strong>İşlem Basamaklarını Yaz</strong>
                <p>Ara hesaplamaları atlamadan göster.</p>
              </div>
            </div>
            <div className={`math-check-item ${data.finalResult.trim() ? 'done' : ''}`}>
              <span className="check-icon">{data.finalResult.trim() ? '✅' : '⚪'}</span>
              <div>
                <strong>Sonucu ve Birimi Netleştir</strong>
                <p>Doğru birim (cm², kg, saat vb.) ile sonucu yaz.</p>
              </div>
            </div>
          </div>
        </div>

        {projectDescription && (
          <div className="card math-info-card">
            <h4>📌 Problem Senaryosu</h4>
            <p>{projectDescription}</p>
          </div>
        )}
      </div>
    </div>
  );
}
